import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import './Files.css';

const Files = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (!supabase) {
        setLoading(false);
        return;
      }

      try {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (!currentUser) {
          setLoading(false);
          return;
        }

        setUser(currentUser);
        await loadFiles(currentUser.id);
      } catch (err) {
        console.error('Files: Error:', err);
        setError(`Tietojen haku epäonnistui: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const loadFiles = async (userId) => {
    try {
      // Hae tiedostot Supabase Storage:sta
      // Polku: userId/birthDataId/filename
      const { data, error: listError } = await supabase.storage
        .from('user_uploads')
        .list(`${userId}/`, {
          limit: 100,
          offset: 0,
        });

      if (listError) {
        // Jos bucketia ei ole vielä luotu tai hakemistoa ei löydy, tämä on ok
        if (listError.message?.includes('not found') || listError.message?.includes('Bucket')) {
          setFiles([]);
          return;
        }
        throw listError;
      }

      // Muodosta tiedostolista: käy läpi kaikki hakemistot (birthDataId)
      const allFiles = [];
      
      if (data) {
        for (const item of data) {
          if (item.id === null) {
            // Hakemisto - hae sen sisältö
            const { data: subData } = await supabase.storage
              .from('user_uploads')
              .list(`${userId}/${item.name}/`, {
                limit: 100,
              });

            if (subData) {
              subData.forEach((file) => {
                if (file.id !== null) {
                  // Tiedosto
                  allFiles.push({
                    ...file,
                    fullPath: `${userId}/${item.name}/${file.name}`,
                    birthDataId: item.name,
                  });
                }
              });
            }
          } else {
            // Tiedosto suoraan userId/ hakemistossa
            allFiles.push({
              ...item,
              fullPath: `${userId}/${item.name}`,
            });
          }
        }
      }

      setFiles(allFiles);
    } catch (err) {
      console.error('Files: Load error:', err);
      setError(`Tiedostojen lataus epäonnistui: ${err.message}`);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setUploading(true);
    setError('');

    try {
      // Hae birth_data user_id:llä
      const { data: birthData } = await supabase
        .from('birth_data')
        .select('id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const birthDataId = birthData?.id || 'general';

      // Upload path: userId/birthDataId/filename
      const filePath = `${user.id}/${birthDataId}/${Date.now()}_${file.name}`;

      const { error: uploadError } = await supabase.storage
        .from('user_uploads')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Reload files
      await loadFiles(user.id);
    } catch (err) {
      console.error('Files: Upload error:', err);
      setError(`Tiedoston lataus epäonnistui: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (filePath) => {
    try {
      const { data, error: downloadError } = await supabase.storage
        .from('user_uploads')
        .createSignedUrl(filePath, 3600);

      if (downloadError) throw downloadError;

      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    } catch (err) {
      console.error('Files: Download error:', err);
      setError(`Tiedoston lataus epäonnistui: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="moon-loader">
          <div className="moon"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="files-page">
      <div className="container">
        <h1>Tiedostot</h1>

        {error && <div className="error-message">{error}</div>}

        <div className="upload-section">
          <label className="upload-label">
            <input
              type="file"
              onChange={handleUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            <span className="btn btn-primary">
              {uploading ? 'Ladataan...' : 'Lataa tiedosto'}
            </span>
          </label>
        </div>

        <div className="files-list">
          {files.length === 0 ? (
            <div className="empty-state">
              <p>Sinulla ei ole vielä tallennettuja tiedostoja.</p>
            </div>
          ) : (
            files.map((file) => {
              const fullPath = file.fullPath || `${user.id}/${file.birthDataId || ''}/${file.name}`;
              const fileSize = file.metadata?.size ? `${(file.metadata.size / 1024).toFixed(1)} KB` : '-';
              const createdAt = file.created_at ? new Date(file.created_at).toLocaleDateString('fi-FI') : '-';
              
              return (
                <div key={file.name || file.fullPath} className="file-item">
                  <div className="file-info">
                    <span className="file-name">{file.name}</span>
                    <span className="file-meta">
                      {fileSize} • {createdAt}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDownload(fullPath)}
                    className="btn btn-secondary"
                  >
                    Lataa
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Files;
